import { gql, type TypedDocumentNode } from '@apollo/client';
import { faLock } from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { isType } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import type { Color } from '@sorare/core/src/style/types';

import type { TaskCardsPicker_anyCardInterface } from './__generated__/index.graphql.ts';

const Lock = styled(Vertical)`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--c-nl-500);
  border-radius: 50%;
`;

const CardsGrid = styled.div`
  display: grid;
  align-items: flex-start;
  gap: var(--double-unit);
  row-gap: var(--triple-unit);

  &:not(.empty) {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    @media ${tabletAndAbove} {
      grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    }
  }
`;

const BottomBar = styled(SBHorizontal)`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  margin-top: auto;
  padding: var(--double-unit) var(--triple-unit);
  border: 1px solid var(--c-nd-100);
  border-radius: var(--double-unit);
  backdrop-filter: blur(64px);
  background: var(--c-nl-800);

  @media ${tabletAndAbove} {
    --card-avatar-size: 48px;
  }
`;

const SlotButtonBase = styled(ButtonBase)`
  position: relative;

  &.selected {
    border: 1px solid var(--c-blue-400);
  }
`;

const CardAvatarWrapper = styled.div`
  position: relative;
  width: var(--card-avatar-size, var(--quadruple-unit));
  min-width: var(--card-avatar-size, var(--quadruple-unit));
  min-height: var(--card-avatar-size, var(--quadruple-unit));
  height: var(--card-avatar-size, var(--quadruple-unit));
  border-radius: 50%;
  border: 1.5px solid var(--color, var(--c-nd-100));
`;

const IconWrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: relative;
  width: min-content;
  min-width: 18px;
  height: 18px;
  margin: -6px auto 0;
  border-radius: 9px;
  background-color: var(--color);
  z-index: 1;
  backdrop-filter: blur(12px);
`;

const LabelWrapper = styled.span`
  position: relative;
  width: min-content;
  margin: -6px auto 0;
  z-index: 1;
  background: transparent;
`;

const BaseSlotContentRoot = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  isolation: isolate;
  align-items: center;
`;

export interface Props {
  children?: ReactNode;
  icon?: ReactNode;
  color: Color;
  showLockedOverlay?: boolean;
  label?: ReactNode;
}

export const BaseSlotContent = ({
  children,
  icon,
  color,
  showLockedOverlay,
  label,
}: Props) => {
  return (
    <BaseSlotContentRoot style={{ '--color': color } as React.CSSProperties}>
      <CardAvatarWrapper>
        {children}
        {!!showLockedOverlay && (
          <Lock>
            <FontAwesomeIcon size="xs" icon={faLock} />
          </Lock>
        )}
      </CardAvatarWrapper>
      {label ? (
        <LabelWrapper>{label}</LabelWrapper>
      ) : (
        <IconWrapper>{icon}</IconWrapper>
      )}
    </BaseSlotContentRoot>
  );
};

const Root = styled(Vertical)`
  flex-grow: 1;
`;

const Button = styled(ButtonBase)`
  position: relative;
  transition: transform 0.2s ease-in-out;
  &:not(:disabled):hover {
    transform: scale(1.05);
  }
`;

export const TaskCardsPicker = <
  T extends TaskCardsPicker_anyCardInterface,
  U extends { slug: string },
>({
  title,
  defaultSlots,
  cards,
  children,
  defaultSelectedSlotIndex = 0,
  renderButton,
  renderSlotContent,
  renderCard,
  clickToUnselect,
  renderWarningDialog,
  extra,
  taskId,
  gameId,
}: {
  title: string;
  taskId?: string;
  defaultSlots: {
    isLocked?: boolean;
    card?: U;
  }[];
  defaultSelectedSlotIndex?: number;
  cards: (T & { isUsedInLineup?: boolean })[];
  children?: ReactNode;
  renderButton: (
    slots: { slug?: string; index: number; gameId?: string }[]
  ) => ReactNode;
  renderSlotContent: ({
    isSelected,
    isLocked,
    card,
    index,
  }: {
    isSelected: boolean;
    card?: T | U;
    index: number;
    isLocked?: boolean;
  }) => ReactNode;
  renderCard: ({
    card,
    cardSlotIndex,
  }: {
    card: NoInfer<T>;
    cardSlotIndex: number;
  }) => ReactNode;
  clickToUnselect?: boolean;
  renderWarningDialog?: ({
    card,
    onCancel,
    onContinue,
  }: {
    card: NoInfer<T>;
    onCancel: () => void;
    onContinue: () => void;
  }) => ReactNode;
  extra?: ReactNode;
  gameId?: string;
}) => {
  const [selectedSlot, setSelectedSlot] = useState<number>(
    !defaultSlots[defaultSelectedSlotIndex].isLocked
      ? defaultSelectedSlotIndex
      : defaultSlots.findIndex(slot => !slot.isLocked)
  );
  const [warningDialogCard, setWarningDialogCard] = useState<T | null>(null);

  const [slots, setSlots] = useState<
    {
      isLocked?: boolean;
      card?: T | U;
      gameId?: string;
    }[]
  >(defaultSlots);

  const nextEditableSlotIndex = slots.findIndex(
    (slot, i) => i > selectedSlot && !slot.isLocked
  );

  const onAddCard = (card: T) => {
    setSlots(prev => {
      const newSlots = [...prev];
      newSlots[selectedSlot] = {
        ...prev[selectedSlot],
        ...{ card, gameId },
      };
      return newSlots;
    });
    if (nextEditableSlotIndex !== -1) {
      setSelectedSlot(nextEditableSlotIndex);
    }
  };

  return (
    <>
      <Root gap={2}>
        <SBHorizontal>
          <LabelL bold>{title}</LabelL>
          {extra}
        </SBHorizontal>
        <CardsGrid
          className={classNames({
            empty: cards.length === 0,
          })}
        >
          {cards.map(card => {
            const isLocked =
              taskId &&
              !!card.usedIn &&
              ((isType(card.usedIn, 'UsedInTasks') &&
                card.usedIn.tasks.filter(tsk => tsk.id !== taskId).length >
                  0) ||
                !isType(card.usedIn, 'UsedInTasks'));
            const cardSlotIndex = slots.findIndex(
              slot =>
                slot.card?.slug === card.slug &&
                (!gameId || slot.gameId === gameId)
            );
            return (
              <Button
                key={card.slug}
                disabled={!!isLocked && !renderWarningDialog}
                onClick={() => {
                  if (clickToUnselect && cardSlotIndex !== -1) {
                    setSlots(prev => {
                      const newSlots = [...prev];
                      newSlots[cardSlotIndex] = {};
                      return newSlots;
                    });
                    setSelectedSlot(cardSlotIndex);
                  } else if (card.isUsedInLineup) {
                    setWarningDialogCard(card);
                  } else if (cardSlotIndex === -1) {
                    onAddCard(card);
                  }
                }}
              >
                {renderCard({ card, cardSlotIndex })}
              </Button>
            );
          })}
          {children}
        </CardsGrid>
        <BottomBar>
          <Horizontal>
            {slots.map((slot, index) => {
              const { card } = slot;
              const isSelected = selectedSlot === index;
              return (
                <SlotButtonBase
                  // eslint-disable-next-line react/no-array-index-key
                  key={`slot-${index}`}
                  onClick={() => {
                    setSelectedSlot(index);
                  }}
                  disabled={slot.isLocked}
                >
                  {renderSlotContent({
                    isSelected,
                    card,
                    index,
                    isLocked: slot.isLocked,
                  })}
                </SlotButtonBase>
              );
            })}
          </Horizontal>
          {renderButton(
            slots.map((slot, index) => {
              if (slot.card?.slug) {
                return { slug: slot.card.slug, index, gameId: slot.gameId };
              }
              return { slug: slot.card?.slug, index };
            })
          )}
        </BottomBar>
      </Root>
      {!!warningDialogCard &&
        !!renderWarningDialog &&
        renderWarningDialog({
          card: warningDialogCard,
          onCancel: () => setWarningDialogCard(null),
          onContinue: () => {
            onAddCard(warningDialogCard);
            setWarningDialogCard(null);
          },
        })}
    </>
  );
};

TaskCardsPicker.fragments = {
  anyCardInterface: gql`
    fragment TaskCardsPicker_anyCardInterface on AnyCardInterface {
      slug
      usedIn(
        so5LeaderboardSlug: $so5LeaderboardSlug
        taskId: $taskId
        gameId: $gameId
      ) {
        slug
        ... on UsedInTasks {
          slug
          tasks {
            id
          }
        }
        ... on UsedInSo5Lineups {
          slug
          so5Lineups {
            id
          }
        }
        ... on ListedOnMarket {
          slug
        }
      }
    }
  ` as TypedDocumentNode<TaskCardsPicker_anyCardInterface>,
};
